import { Card, Grid, Icon, IconButton, InputLabel, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import MDAuditModal from "components/MDAuditModal";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { trueOrFalseEnum } from "utils/Enums";
import { post, get } from "utils/WebService";
import ContaBancariaCreateEdit from "./ContaBancariaCreateEdit";

export default function ContaBancariaConsult() {

    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const [contaData, setContaData] = useState([]);
    const [contaDataPages, setContaDataPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(true);
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [selectedContaId, setSelectedContaId] = useState(null);
    const [refresh, setRefresh] = useState(false);
    const [recordsTotal, setRecordsTotal] = useState(0);
    const [aplicar, setAplicar] = useState(false);

    const [auditModalOpen, setAuditModalOpen] = useState(false);
    const [auditTitle, setAuditTitle] = useState("");
    const [auditLogs, setAuditLogs] = useState([]);
    const [loadingAudit, setLoadingAudit] = useState(false);

    const handleOpenAuditoriaRow = (row) => {
        const id = row.id || row.cvId;
        setAuditTitle(`Auditoria da Conta: ${row.descricao || ("ID " + id)}`);
        setAuditModalOpen(true);
        setLoadingAudit(true);
        get(`/conta-bancaria/${id}/historico`)
            .then(res => setAuditLogs(Array.isArray(res) ? res : []))
            .catch(err => {
                console.error("Erro ao carregar auditoria de conta bancária:", err);
                setAuditLogs([]);
            })
            .finally(() => setLoadingAudit(false));
    };

    const handleOpenAuditoriaGeral = () => {
        setAuditTitle("Histórico de Auditoria - Todas as Contas Bancárias");
        setAuditModalOpen(true);
        setLoadingAudit(true);
        get("/conta-bancaria/historico-geral")
            .then(res => setAuditLogs(Array.isArray(res) ? res : []))
            .catch(err => {
                console.error("Erro ao carregar auditoria geral de contas bancárias:", err);
                setAuditLogs([]);
            })
            .finally(() => setLoadingAudit(false));
    };

    const [filters, setFilters] = useState({
        banco: '',
        ativo: trueOrFalseEnum[1]
    });

    const [bancosEnum, setBancosEnum] = useState([{ value: '', label: 'Todos' }]);

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: []
    });

    useEffect(() => {
        let filtered = [];
        filtered.push({ id: 'ativo', value: true });
        fetchData({ ...tableState.current, filtered });

        get("/conta-bancaria/list-all").then((data) => {
             const uniqueBancosMap = new Map();
             data.forEach(item => {
                 if (item.banco && item.descricao) {
                     uniqueBancosMap.set(item.banco, item.descricao);
                 }
             });
             const enumBancos = Array.from(uniqueBancosMap.entries()).map(([code, name]) => ({ value: code, label: name }));
             setBancosEnum([{ value: '', label: 'Todos' }, ...enumBancos]);
        }).catch(() => {});
    }, []);

    const fetchData = useCallback(({ pageSize, pageIndex, sorting, filtered }) => {
        setLoadingTable(true);

        const pageable = {
            pageSize,
            page: pageIndex,
            sorting,
            filtered
        };

        post("/conta-bancaria/list", pageable).then(response => {
            setContaData(response.data);
            setContaDataPages(response.pages);
            setRecordsTotal(response.recordsTotal);
            setLoadingTable(false);
        }).catch(() => {
            setContaData([]);
            setLoadingTable(false);
        });

        tableState.current = { pageSize, pageIndex, sorting, filtered };
    }, [refresh]);

    const handleRefresh = () => {
        setRefresh(prev => !prev);
    };

    const handleCreateEdit = (id = null) => {
        setSelectedContaId(id);
        setIsCreateEdit(true);
    };

    const closeCreateEdit = () => {
        setIsCreateEdit(false);
        setSelectedContaId(null);
        handleRefresh();
    };

    const handleChangeFilter = (prop) => (event) => {
        setFilters({ ...filters, [prop]: event.target.value });
    };

    const handleApplyFilter = () => {
        const newFiltered = [];

        if (filters.banco) {
            newFiltered.push({ id: 'banco', value: filters.banco });
        }
        if (filters.ativo && filters.ativo.value !== '') {
            newFiltered.push({ id: 'ativo', value: filters.ativo.value });
        }

        tableState.current = { ...tableState.current, pageIndex: 0, filtered: newFiltered };
        setAplicar(!aplicar);
    };

    const columns = [
        {
            header: "Descrição",
            accessorKey: "descricao",
            cell: (info) => <DefaultCell>{info.getValue() || ''}</DefaultCell>,
            size: 250,
        },
        {
            header: "Banco",
            accessorKey: "banco",
            cell: (info) => <DefaultCell>{info.getValue() || ''}</DefaultCell>,
            size: 100,
        },
        {
            header: "Agência",
            accessorKey: "agencia",
            cell: (info) => <DefaultCell>{`${info.row.original.agencia || ''}${info.row.original.agenciaDigito ? '-' + info.row.original.agenciaDigito : ''}`}</DefaultCell>,
            size: 120,
        },
        {
            header: "Conta",
            accessorKey: "conta",
            cell: (info) => <DefaultCell>{`${info.row.original.conta || ''}${info.row.original.contaDigito ? '-' + info.row.original.contaDigito : ''}`}</DefaultCell>,
            size: 120,
        },
        {
            header: "Ativo",
            accessorKey: "ativo",
            cell: (cell) => (
                <MDBox display="flex" alignItems="center">
                    <Icon
                        color={cell.getValue() ? "success" : "error"}
                        sx={{ ml: 1, fontSize: "1.25rem!important" }}
                    >
                        {cell.getValue() ? "check_circle" : "cancel"}
                    </Icon>
                </MDBox>
            ),
            size: 100,
        },
        {
            header: "Ações",
            accessorKey: "acoes",
            cell: (info) => (
                <MDBox display="flex" alignItems="center" justifyContent="center">
                    <Tooltip title="Editar" placement="top">
                        <IconButton
                            color="info"
                            onClick={() => handleCreateEdit(info.row.original.id)}
                        >
                            <Icon>edit</Icon>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Histórico de Auditoria" placement="top">
                        <IconButton
                            color="info"
                            onClick={() => handleOpenAuditoriaRow(info.row.original)}
                        >
                            <Icon>history</Icon>
                        </IconButton>
                    </Tooltip>
                </MDBox>
            ),
            size: 100,
            enableSorting: false,
        }
    ];

    const handleCleanFilter = () => {
        setFilters({
            banco: '',
            ativo: trueOrFalseEnum[1]
        });
        tableState.current = { ...tableState.current, pageIndex: 0, filtered: [] };
        setAplicar(true);
        fetchData(tableState.current);
    };

    return (
        <Grid container display="flex" justifyContent="center">
            {isCreateEdit &&
                <Grid item xs={12} md={12}>
                    <ContaBancariaCreateEdit
                        contaId={selectedContaId}
                        closeCreateEdit={closeCreateEdit}
                    />
                </Grid>
            }
            <Grid item xs={12} md={12} sx={{ display: isCreateEdit ? "none" : "" }}>
                <Card
                    variant="outlined"
                    sx={{
                        px: 2,
                        py: 1.5,
                        mb: 2,
                        background: darkMode ? "#4B4F5C" : "#fafafa",
                        borderColor: darkMode ? "#333" : "#ddd",
                    }}
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Nome Banco</InputLabel>
                            <MDSelect
                                placeholder="Selecione o banco"
                                options={bancosEnum}
                                value={filters.banco}
                                onChange={(newValue) => setFilters({ ...filters, banco: newValue })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Ativo</InputLabel>
                            <MDSelect
                                placeholder="Selecione"
                                options={trueOrFalseEnum}
                                value={filters.ativo}
                                onChange={(newValue) => setFilters({ ...filters, ativo: newValue })}
                            />
                        </Grid>
                        <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between" pt={1}>
                            <MDBox display="flex" gap={1}>
                                <MDButton
                                    size="medium"
                                    variant="contained"
                                    color="info"
                                    circular={false}
                                    iconOnly={false}
                                    onClick={() => handleCreateEdit(null)}>
                                    Adicionar conta
                                </MDButton>
                                <MDButton
                                    size="medium"
                                    variant="outlined"
                                    color="info"
                                    circular={false}
                                    iconOnly={false}
                                    onClick={handleOpenAuditoriaGeral}>
                                    <Icon sx={{ mr: 0.5 }}>history</Icon> Auditoria Geral
                                </MDButton>
                            </MDBox>
                            <MDBox>
                                <MDButton
                                    size="medium"
                                    sx={{ marginRight: 2 }}
                                    variant="contained"
                                    color="info"
                                    circular={false}
                                    iconOnly={false}
                                    onClick={handleCleanFilter}>
                                    Limpar filtro
                                </MDButton>
                                <MDButton
                                    size="medium"
                                    variant="contained"
                                    color="info"
                                    circular={false}
                                    iconOnly={false}
                                    onClick={handleApplyFilter}>
                                    Aplicar filtro
                                </MDButton>
                            </MDBox>
                        </Grid>
                    </Grid>
                </Card>
                <DataTable
                    tableState={tableState}
                    data={contaData}
                    columnData={columns}
                    totalPages={contaDataPages}
                    totalRecords={recordsTotal}
                    loading={loadingTable}
                    fetchData={fetchData}
                    changeFilter={aplicar}
                />
            </Grid>

            <MDAuditModal
                open={auditModalOpen}
                onClose={() => setAuditModalOpen(false)}
                title={auditTitle}
                loading={loadingAudit}
                logs={auditLogs}
                entityLabel="Conta Bancária"
            />
        </Grid>
    );
}
